import { Button, Drawer, Form, Input, message, Radio, Select, Space } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { addRule } from '@/api';
import { DeviceLibraries } from '@/api/device-libraries';
import { getProductList } from '@/api/product';
import { useRequest } from 'ahooks';

const { useForm } = Form;
const { TextArea } = Input;

export interface AddRuleModalProps {
  onFinish: () => void;
}
export interface AddRuleModalRef {
  open: (record?: DeviceLibraries) => void;
}

export const AddRuleModal = forwardRef<AddRuleModalRef, AddRuleModalProps>(({ onFinish }, ref) => {
  const [form] = useForm();
  const [record, setRecord] = useState<DeviceLibraries>();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // 获取产品列表
  const { data: productData } = useRequest(() => getProductList({ pageSize: 100 }), {
    refreshDeps: [],
  });
  const productList = productData?.result?.list || [];

  // const dockerConfigModalRef = useRef<DockerConfigModalRef>(null)

  const handleClose = useCallback(() => {
    form.resetFields();
    setIsModalOpen(false);
  }, [form]);

  useImperativeHandle(
    ref,
    () => {
      return {
        open(record) {
          setRecord(record);
          setIsModalOpen(true);
          if (record) {
            form.setFieldsValue(record);
          }
        },
      };
    },
    [form],
  );

  return (
    <Drawer
      title={record ? '编辑规则' : '添加规则'}
      placement="right"
      open={isModalOpen}
      width={550}
      closable={false}
      maskClosable={false}
      styles={{ header: { height: 51 } }}
      destroyOnHidden
      extra={<CloseOutlined onClick={handleClose} />}
      onClose={handleClose}
      footer={
        <Space>
          <Button
            type="primary"
            loading={submitting}
            disabled={submitting}
            onClick={() => {
              form.validateFields().then((values) => {
                setSubmitting(true);
                addRule({ ...values, product_id: values.product_id })
                  .then((resp) => {
                    if (resp.success) {
                      message.success('添加成功');
                      handleClose();
                      onFinish();
                    } else {
                      message.error(resp.errorMsg);
                    }
                  })
                  .finally(() => {
                    setSubmitting(false);
                  });
              });
            }}
          >
            确定
          </Button>
          <Button onClick={handleClose}>取消</Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="规则名称"
          name="name"
          rules={[
            {
              required: true,
              validator(rule, value, callback) {
                if (!value) {
                  callback('请输入规则名称');
                } else if (/^[a-zA-Z\u4e00-\u9fa5][a-zA-Z0-9\u4e00-\u9fa5_-]{0,31}$/.test(value)) {
                  callback();
                } else {
                  callback('1-32位字符，支持中文、英文、数字及特殊字符_-，必须以英文或中文字符开头');
                }
              },
            },
          ]}
        >
          <Input placeholder="1-32位字符，支持中文、英文、数字及特殊字符_-，必须以英文或中文字符开头"></Input>
        </Form.Item>
        <Form.Item label="告警类型" name="type">
          <Radio checked>设备告警</Radio>
        </Form.Item>
        <Form.Item label="所属产品" name="product_id" rules={[{ required: true, message: '请选择所属产品' }]}>
          <Select
            placeholder="请选择产品"
            showSearch
            optionFilterProp="name"
            options={productList.map((p: { id: string; name: string }) => ({ value: p.id, label: p.name }))}
          />
        </Form.Item>
        <Form.Item label="告警级别" name="alert_level" rules={[{ required: true, message: '请选择告警级别' }]}>
          <Select
            placeholder="请选择"
            options={[
              { value: '紧急', label: '紧急' },
              { value: '重要', label: '重要' },
              { value: '次要', label: '次要' },
              { value: '提示', label: '提示' },
            ]}
          />
        </Form.Item>
        <Form.Item label="规则描述" name="description">
          <TextArea showCount maxLength={100} placeholder="请输入描述信息" rows={4}></TextArea>
        </Form.Item>
      </Form>
    </Drawer>
  );
});

export default AddRuleModal;
